import { Theme, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import React from 'react'
import Select from 'react-select'

import TemplateInitialsSquare from './TemplateInitialsSquare'
import { TickIcon } from 'components/icons'
import useTemplateId from 'hooks/useTemplateId'
import { Template } from 'types/template'

function getOptionWrapperBackgroundColor(theme: Theme, isFocused: boolean): string {
	if (isFocused) {
		return theme.colors.light.surfaceVariantHover
	}

	if (theme.isMobile) {
		return theme.colors.light.surface
	}

	return theme.colors.light.primaryBackgroundColor
}

const OptionWrapper = styled.div<{ isFocused?: boolean; isInMenu?: boolean }>`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.spaces.space5};
	padding: ${({ theme, isInMenu }) =>
		isInMenu ? `${theme.spaces.space3} ${theme.spaces.space5}` : theme.spaces.space6};
	background-color: ${({ isFocused, theme }) => getOptionWrapperBackgroundColor(theme, Boolean(isFocused))};
	border-radius: ${({ theme }) => theme.borderRadiuses.xl};
	cursor: pointer;
	transition: background-color 100ms linear;
`

const OptionTextSection = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	justify-content: center;
`

const OptionCaption = styled.span`
	color: ${({ theme }) => theme.colors.light.tertiaryTextColor};
	${({ theme }) => theme.typography.smallcaps};
`

const OptionName = styled.span`
	${({ theme }) => theme.typography.captionMedium};
`

const OptionTickCircle = styled.span`
	height: 16px;
	width: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${({ theme }) => theme.colors.light.surface};
	background-color: ${({ theme }) => theme.colors.light.primaryTextColor};
	border-radius: ${({ theme }) => theme.borderRadiuses.round};
`

function OptionPill({
	isSelected,
	isFocused,
	children,
	innerRef,
	innerProps,
	data,
}: {
	isSelected?: boolean
	isFocused?: boolean
	children: React.ReactNode
	innerRef?: React.Ref<HTMLDivElement>
	innerProps?: React.HTMLAttributes<HTMLDivElement>
	data: { value: string; label: string }
}) {
	const isInMenu = typeof innerProps !== 'undefined'

	return (
		<OptionWrapper
			data-testid={`template-chooser-option-${isInMenu ? 'menu' : 'input'}`}
			isFocused={isFocused}
			isInMenu={isInMenu}
			{...(innerRef && { ref: innerRef })}
			{...innerProps}
		>
			<TemplateInitialsSquare name={data.label} data-testid="template-initials-box" />
			<OptionTextSection>
				{!isInMenu && <OptionCaption>Template</OptionCaption>}
				<OptionName>{children}</OptionName>
			</OptionTextSection>
			{isSelected && (
				<OptionTickCircle>
					<TickIcon size={12} />
				</OptionTickCircle>
			)}
		</OptionWrapper>
	)
}

function getSelectBorderColor(theme: Theme, isFocused: boolean): string {
	if (isFocused) {
		return theme.colors.light.surfaceInteractive
	}

	if (theme.isMobile) {
		return theme.colors.light.borderColor
	}

	return 'transparent'
}

type Option = {
	value: string
	label: string
}

type Props = {
	templates: Template[]
	controlled?: {
		value?: string
		onChange: (value: string) => void
	}
	elevated?: boolean
	dropdownPosition?: 'bottom' | 'top'
}

function TemplateChooser({ templates, controlled, elevated, dropdownPosition = 'bottom' }: Props) {
	const theme = useTheme()
	const { templateId, setUrlQueryTemplateId } = useTemplateId()

	function handleTemplateSelect(option: Option | null) {
		if (option !== null) {
			if (controlled) {
				controlled.onChange(option.value)
			} else {
				setUrlQueryTemplateId(option.value)
			}
		}
	}

	const options = React.useMemo(
		() => templates.map((template) => ({ value: template.id, label: template.name })),
		[templates],
	)

	const defaultValue = React.useMemo(
		() => options.find((option) => option.value === (controlled?.value ?? templateId ?? 0)),
		[options, templateId, controlled?.value],
	)

	const currentValue = React.useMemo(
		() => options.find((option) => option.value === (controlled?.value ?? templateId)),
		[options, templateId, controlled?.value],
	)

	return (
		<Select
			placeholder="Select template"
			aria-label="Select template"
			id="template-chooser"
			isClearable={false}
			isSearchable={false}
			closeMenuOnSelect={true}
			value={currentValue}
			onChange={handleTemplateSelect}
			options={options}
			defaultValue={defaultValue}
			styles={{
				container: (baseStyles) => ({
					...baseStyles,
					zIndex: 5,
					borderColor: 'transparent',
				}),
				control: (baseStyles, state) => ({
					...baseStyles,
					borderRadius: theme.borderRadiuses.xl,
					backgroundColor: theme.isMobile ? theme.colors.light.surface : theme.colors.light.primaryBackgroundColor,
					boxShadow: theme.isMobile || elevated ? 'none' : theme.boxShadows.lg,
					borderColor: elevated ? theme.colors.light.borderColor : getSelectBorderColor(theme, state.isFocused),
					overflow: 'hidden',
					...(!state.hasValue && { padding: theme.spaces.space3 }),
				}),
				indicatorSeparator: (baseStyles) => ({
					...baseStyles,
					display: 'none',
				}),
				indicatorsContainer: (baseStyles) => ({
					...baseStyles,
					paddingRight: theme.spaces.space2,
				}),
				dropdownIndicator: (baseStyles) => ({
					...baseStyles,
					color: theme.colors.light.primaryTextColor,
				}),
				valueContainer: (baseStyles) => ({
					...baseStyles,
					display: 'flex',
					padding: 0,
				}),
				menu: (baseStyles) => ({
					...baseStyles,
					padding: theme.spaces.space2,
					borderRadius: theme.borderRadiuses.xl,
					border: 'none',
					backgroundColor: theme.colors.light.primaryBackgroundColor,
					boxShadow: theme.boxShadows.lg,
					...(dropdownPosition === 'top' && {
						bottom: '100%',
						top: 'auto',
						marginBottom: theme.spaces.space2,
						marginTop: 0,
					}),
				}),
				menuList: (baseStyles) => ({
					...baseStyles,
					display: 'flex',
					flexDirection: 'column',
					gap: theme.spaces.space3,
					paddingTop: 0,
					paddingBottom: 0,
				}),
			}}
			theme={(baseTheme) => ({
				...baseTheme,
				colors: {
					...baseTheme.colors,
					primary: theme.colors.light.primaryTextColor,
				},
			})}
			components={{
				SingleValue: OptionPill,
				Option: OptionPill,
			}}
		/>
	)
}

export default TemplateChooser
